import React, { useState, useMemo } from "react";
import {
  ListGroup,
  Form,
  InputGroup,
  FormControl,
  Pagination,
  Button,
} from "react-bootstrap";

const PanelsMenu = ({ data, selectedPanels, setSelectedPanels, disable }) => {
  const itemsPerPage = 10; // Number of items per page
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState(""); // Manage search input

  // Filter panels based on search term
  const filteredPanels = useMemo(() => {
    return data.filter((panel) =>
      panel.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [data, searchTerm]);

  // Total number of pages after filtering
  const totalPages = Math.ceil(filteredPanels.length / itemsPerPage);

  // Get the current list of panels to show based on pagination
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentPanels = filteredPanels.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  // Update selected panels
  const handleTogglePanel = (panel) => {
    if (selectedPanels.some((p) => p.id === panel.id)) {
      setSelectedPanels(selectedPanels.filter((p) => p.id !== panel.id));
    } else {
      setSelectedPanels([...selectedPanels, panel]);
    }
  };

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Reset page when search term changes
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to page 1 on search
  };

  // Generate pagination with ellipsis
  const getPaginationItems = () => {
    const paginationItems = [];
    const maxVisiblePages = 3; // Number of pages to display at once around current page
    const rangeStart = Math.max(
      2,
      currentPage - Math.floor(maxVisiblePages / 2)
    );
    const rangeEnd = Math.min(
      totalPages - 1,
      currentPage + Math.floor(maxVisiblePages / 2)
    );

    // Always show the first page
    paginationItems.push(
      <Pagination.Item
        key={1}
        active={currentPage === 1}
        onClick={() => handlePageChange(1)}
      >
        1
      </Pagination.Item>
    );

    // Show ellipsis if current page is beyond page 4
    if (rangeStart > 2) {
      paginationItems.push(<Pagination.Ellipsis key="start-ellipsis" />);
    }

    // Show pages around the current page
    for (let i = rangeStart; i <= rangeEnd; i++) {
      paginationItems.push(
        <Pagination.Item
          key={i}
          active={currentPage === i}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </Pagination.Item>
      );
    }

    // Show ellipsis if we haven't reached the last page
    if (rangeEnd < totalPages - 1) {
      paginationItems.push(<Pagination.Ellipsis key="end-ellipsis" />);
    }

    // Always show the last page if there are more than one page
    if (totalPages > 1) {
      paginationItems.push(
        <Pagination.Item
          key={totalPages}
          active={currentPage === totalPages}
          onClick={() => handlePageChange(totalPages)}
        >
          {totalPages}
        </Pagination.Item>
      );
    }

    return paginationItems;
  };

  return (
    <div>
      {/* Search Bar */}
      <p>
        <strong>Panels Filter:</strong>
      </p>
      <InputGroup className="mb-3">
        {/* <InputGroup.Text>Panels Search</InputGroup.Text> */}
        <FormControl
          placeholder="Filter panels by name"
          value={searchTerm}
          onChange={handleSearchChange}
          disabled={disable}
        />
      </InputGroup>
      {/* Selected Panels */}
      <div className="mb-3 d-flex justify-content-between align-items-center">
        <div style={{ maxWidth: "70%" }}>
          <p className="mb-1">
            <strong>Selected Panels:</strong>{" "}
            <span className="badge bg-primary ms-1">
              {selectedPanels.length}
            </span>
          </p>
          {selectedPanels.length > 0 && (
            <small className="text-muted text-truncate d-block">
              {selectedPanels
                .slice(0, 3)
                .map((panel) => panel.name)
                .join(", ")}
              {selectedPanels.length > 3 &&
                ` ... and ${selectedPanels.length - 3} more`}
            </small>
          )}
        </div>
        <Button
          onClick={() => setSelectedPanels([])}
          variant="danger"
          className="mb-2 btn-animate"
          disabled={disable || selectedPanels.length === 0} // Disable button when menu is disable
        >
          Clear
        </Button>
      </div>

      {/* List of Filtered Panels(name number_of_genes description), including checkboxes */}
      <ListGroup>
        {currentPanels.map((panel) => (
          <ListGroup.Item
            key={panel.id}
            className="d-flex justify-content-between align-items-center clickable-panel-item"
            onClick={() => !disable && handleTogglePanel(panel)}
            style={{ cursor: disable ? "default" : "pointer" }}
          >
            <span>
              {panel.name}{" "}
              <small className="text-muted">
                ({panel.number_of_genes} genes)
              </small>
            </span>
            <Form.Check
              type="checkbox"
              checked={selectedPanels.some((p) => p.id === panel.id)}
              onChange={() => {}} // Handled by parent item click
              disabled={disable}
              style={{ pointerEvents: "none" }} // Prevent double-clicking
            />
          </ListGroup.Item>
        ))}
      </ListGroup>

      <div className="d-flex justify-content-center mt-3">
        {/* Pagination */}
        <Pagination>
          <Pagination.Prev
            onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
          />
          {getPaginationItems()}
          <Pagination.Next
            onClick={() =>
              handlePageChange(Math.min(totalPages, currentPage + 1))
            }
            disabled={currentPage === totalPages}
          />
        </Pagination>
      </div>
    </div>
  );
};

export default PanelsMenu;
